/**
 * Formatierung (de-DE, EUR, Europe/Berlin). Beträge kommen von der API als String "1234.50".
 */
const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const num = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
const num2 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export type Money = string | number | null | undefined;

/** "1234.50" → Cent (exakt, ohne Float-Fehler) */
export function toCents(v: Money): number {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Math.round(v * 100);
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?/.exec(v.trim());
  if (!m) return 0;
  const c = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
  return m[1] ? -c : c;
}

export function centsToApi(c: number): string {
  const sign = c < 0 ? '-' : '';
  const a = Math.abs(c);
  return `${sign}${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
}

export function formatEuro(v: Money, opts: { hideZero?: boolean; signed?: boolean } = {}): string {
  const c = toCents(v);
  if (opts.hideZero && c === 0) return '–';
  const s = eur.format(c / 100);
  return opts.signed && c > 0 ? `+${s}` : s;
}

export function formatAmount(v: Money): string {
  return num2.format(toCents(v) / 100);
}

export function formatNumber(v: number | string | null | undefined, digits?: number): string {
  if (v === null || v === undefined || v === '') return '–';
  const n = Number(v);
  return digits === undefined ? num.format(n) : new Intl.NumberFormat('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

/** Deutsche Eingabe "1.234,56" / "1234,5" / "1234.56" → API-String "1234.56" (oder null bei Fehler) */
export function parseAmountInput(input: string): string | null {
  let s = input.replace(/[€\s]/g, '').trim();
  if (!s) return null;
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [e, f = ''] = s.split('.');
  return `${neg ? '-' : ''}${Number(e)}.${f.padEnd(2, '0')}`;
}

/** Für Eingabefelder: API-String "1234.5" → "1.234,50" */
export function amountToInput(v: Money): string {
  if (v === null || v === undefined || v === '') return '';
  return num2.format(toCents(v) / 100);
}

/** "2026-09-03" → "03.09.2026" */
export function formatDate(d: string | null | undefined): string {
  if (!d) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return d;
  // Zeitstempel (mit Uhrzeit) in Berliner Zeit darstellen
  if (d.length > 10) {
    return new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(d));
  }
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export function formatDateTime(d: string | null | undefined): string {
  if (!d) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(d));
}

export const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
export const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** "2026-09" → "September 2026" */
export function formatPeriod(p: string | null | undefined): string {
  if (!p) return '–';
  const [y, m] = p.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export function currentPeriod(): string {
  return todayIso().slice(0, 7);
}

export function shiftPeriod(p: string, delta: number): string {
  const [y, m] = p.split('-').map(Number);
  const t = y * 12 + (m - 1) + delta;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}

export function formatIban(iban: string | null | undefined): string {
  if (!iban) return '–';
  return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

export function personName(p: { firstName?: string | null; lastName?: string | null; companyName?: string | null } | null | undefined): string {
  if (!p) return '–';
  if (p.companyName && !p.firstName) return p.companyName;
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || p.companyName || '–';
}
